import { createTemporaryId, isCreated } from '../utils/model';
import { PredefinedValue } from './PredefinedValue';
import { ProductConfig } from './ProductConfig';
import User, { UserType } from './User';

export type ProductImage = {
  uri: string;
  mimetype: string;
};

export enum InputType {
  None = 'none',
  Quantity = 'quantity',
  Price = 'price'
}

export class ProductFieldReference {
  readonly fieldId: string;
  readonly value: string = "";
  readonly isInput?: boolean;
  readonly predefinedValues: PredefinedValue[] = [];

  constructor(deriveFrom?: Partial<ProductFieldReference>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    this.predefinedValues = this.predefinedValues.map(element =>
      new PredefinedValue(element));
  }
}

/**
 * A single product.
 */
export default class Product {
  readonly id: string = createTemporaryId();
  readonly key: string = createTemporaryId();
  readonly name: string = '';
  /**
   * Default price, instead of a specific price model.
   */
  readonly price: number = 0;
  readonly priceMin?: number;
  readonly priceMax?: number;
  readonly priceOptions: number[] = [];
  readonly configs: ProductConfig[] = [];
  readonly salaryBasis: number = null;
  readonly categoryId: string = '';
  readonly image?: ProductImage;
  readonly fields: ProductFieldReference[] = [];
  readonly vat: number = 25;
  readonly createdAt: Date = new Date();
  /**
   * When selecting this product, this determines what information the user set.
   */
  readonly inputType: InputType = InputType.None;

  /**
   * Whether to list this product in express flows, presuming the express flow
   * is configured to show the product's category.
   */
  readonly isVisibleInExpressFlows: boolean = true;
  readonly isVisibleInWebForms: boolean = true;
  /**
   * For calendar services, an object of operators which resolves the duration of a new meeting, in minutes.
   */
  readonly calendarEventDuration: any = { _value: null };
  readonly defaultConfig: ProductConfig;

  private readonly fieldValues: { [fieldId: string]: string } = {}

  constructor(deriveFrom?: Partial<Product>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    this.createdAt = new Date(this.createdAt);

    if (this.fields) {
      this.fieldValues = this.fields.reduce((object, { fieldId, value }) => {
        return { ...object, [fieldId]: value };
      }, {});
    }

    this.fields = this.fields.map(reference =>
      new ProductFieldReference(reference));

    this.configs = this.configs.map(config => new ProductConfig(config));

    this.defaultConfig = new ProductConfig({
      priceMax: this.priceMax,
      priceMin: this.priceMin,
      priceOptions: this.priceOptions,
      price: this.price,
      teamIds: [],
    });
  }

  get isCreated() {
    return isCreated(this.id);
  }

  get salaryBasedPrice() {
    return this.salaryBasis || this.price;
  }

  getFieldValue(productFieldId: string): string | null {
    return this.fieldValues[productFieldId] || null;
  }

  getConfig(user: User): ProductConfig {
    const teamIds = user.listTeamIds();

    let config = this.configs.find(config =>
      config.teamIds.find(teamId => teamIds.includes(teamId))
    );
    
    if (!config && user.type === UserType.Admin) {
      config = this.configs.find(config => !config.teamIds.length);
    }

    return config ?? this.defaultConfig;
  }
}